import { BasePayloadMapper } from '@ps/utils';
import { Option } from '@sapphire/result';

import {
  AnswerOption,
  DataQuality,
  Dependency,
  DynamicProfiler,
  Localization,
  Segmentation,
} from '../../models/localization.model';
import {
  AnswerOptionDto,
  DataQualityDto,
  DependencyDto,
  DynamicProfilerDto,
  LocalizationDto,
  SegmentationDto,
} from '../dtos/localization.dto';

export class LocalizationPayloadMapper extends BasePayloadMapper<
  Localization,
  LocalizationDto
> {
  public toPayload(
    model: Localization,
    isPrivacyPolicyQuestion = false,
  ): LocalizationDto {
    const isEnglish = this._isEnglishLocale(model.locale);

    return {
      locale: model.locale,
      text: model.text,
      image_url: model.imageUrl,
      create_survey_position: model.createSurveyPosition,
      create_survey_component: model.createSurveyComponent,
      name_alias: model.nameAlias,
      has_census_data: model.hasCensusData,
      stats: { total: model.totalAnswered },
      placeholder: model.placeholder,
      answers: model.answers.map((answer) =>
        this._toAnswerDto(answer, isEnglish),
      ),
      dynamic_profiler: this._toDynamicProfilerDto(model.dynamicProfiler),
      hasSegmentation: model.hasSegmentation,
      ...(model.hasSegmentation &&
        model.segmentation && {
          segmentation: this._toSegmentationDto(model.segmentation),
        }),
      hasDependency: model.hasDependency,
      ...(model.hasDependency &&
        model.dependency && {
          dependency: this._toDependencyDto(model.dependency),
        }),
      ...(model.dataQuality && {
        data_quality: this._toDataQualityDto(model.dataQuality),
      }),
      ...(isPrivacyPolicyQuestion && {
        non_gdpr: model.nonGdpr,
        pp_text: model.ppText,
        pp_modal_text: model.ppModalText,
      }),
      ...(model.format && { format: model.format }),
      qualification_type:
        model.segmentation?.childQualificationId != null ? 'dependent' : false,
      eligibleForOETermination: model.dataQuality
        ? model.dataQuality.eligibleForOeTermination
        : false,
    };
  }

  private _isEnglishLocale(locale: string): boolean {
    return locale.split('_')[0] === 'en';
  }

  private _toDependencyDto(model: Dependency): DependencyDto {
    return {
      parent_qualification_id: model.parentQualificationId,
      match_criteria: model.matchCriteria,
      condition: model.condition,
    };
  }

  private _toSegmentationDto(model: Segmentation): SegmentationDto {
    return {
      parent_qualification_id: model.parentQualificationId,
      child_qualification_id: model.childQualificationId,
      condition: model.condition,
    };
  }

  private _toDataQualityDto(model: DataQuality): DataQualityDto {
    return {
      enable: model.enable,
      eligible_for_oe_termination: model.eligibleForOeTermination,
      quality_rules: model.qualityRules,
    };
  }

  private _toDynamicProfilerDto(model: DynamicProfiler): DynamicProfilerDto {
    return {
      enable: model.enable,
      enable_by_HT: Option.from(model.enableByHt).unwrapOr(undefined),
      priority: model.priority,
      active_survey_count: Option.from(model.activeSurveyCount).unwrapOr(
        undefined,
      ),
    };
  }

  private _toAnswerDto(
    model: AnswerOption,
    isEnglish: boolean,
  ): AnswerOptionDto {
    return {
      condition_code: model.id,
      answer_id: String(model.id),
      eng_text: isEnglish ? model.text : model.translation,
      local_text: model.text,
    };
  }
}
